import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../utils/authUtils';

export type UsageFrequency =
  | 'active_daily'
  | 'active_weekly'
  | 'occasional'
  | 'stopped'
  | 'tried_once';

export type AbandonmentReason =
  | 'no_results'
  | 'confusing_ui'
  | 'too_expensive'
  | 'technical_issues'
  | 'found_alternative'
  | 'not_job_searching'
  | 'forgot_about_it'
  | 'slow_performance'
  | 'privacy_concerns'
  | 'other';

export type FeatureUsed =
  | 'resume_optimizer'
  | 'job_matching'
  | 'interview_prep'
  | 'apply_tracking'
  | 'ai_agent'
  | 'resume_builder'
  | 'job_board'
  | 'none';

export type ImprovementPriority =
  | 'better_job_matches'
  | 'easier_resume_tools'
  | 'lower_pricing'
  | 'faster_performance'
  | 'better_mobile'
  | 'more_guidance'
  | 'other';

export type WouldRecommend = 'yes' | 'maybe' | 'no';

export type ProductFeedbackPayload = {
  usage_frequency: UsageFrequency;
  overall_rating: number;
  abandonment_reasons: AbandonmentReason[];
  problems_faced: string;
  features_used: FeatureUsed[];
  improvement_priority: ImprovementPriority;
  would_recommend: WouldRecommend;
  additional_comments: string;
  survey_wave?: string;
};

export type ProductFeedbackSurvey = ProductFeedbackPayload & {
  id: string;
  user_id: string;
  created_at: string;
};

export async function submitProductFeedbackSurvey(
  payload: ProductFeedbackPayload,
): Promise<ProductFeedbackSurvey> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('auth_required');

  const { data, error } = await supabase
    .from('product_feedback_surveys')
    .insert({
      user_id: userId,
      usage_frequency: payload.usage_frequency,
      overall_rating: payload.overall_rating,
      abandonment_reasons: payload.abandonment_reasons,
      problems_faced: payload.problems_faced.trim() || null,
      features_used: payload.features_used,
      improvement_priority: payload.improvement_priority,
      would_recommend: payload.would_recommend,
      additional_comments: payload.additional_comments.trim() || null,
      survey_wave: payload.survey_wave ?? '2026-q1',
    })
    .select()
    .single();

  if (error) throw error;
  return data as ProductFeedbackSurvey;
}

export type ProductFeedbackOverview = {
  survey_wave: string;
  registered_users: number;
  invites_sent: number;
  invites_failed?: number;
  pending_invites?: number;
  responses: number;
  avg_rating: number | null;
  would_recommend_yes: number;
  would_recommend_no: number;
};

export type ProductFeedbackInviteRow = {
  user_id: string;
  email: string | null;
  name: string | null;
  sent_at: string;
  send_status: 'sent' | 'failed';
  error_message: string | null;
  has_responded: boolean;
  responded_at: string | null;
};

export type ProductFeedbackSurveyRow = ProductFeedbackSurvey & {
  user_email?: string | null;
  user_name?: string | null;
};

export type InviteBatchResult = {
  ok?: boolean;
  sent: number;
  skipped: number;
  failed: number;
  total: number;
  survey_wave?: string;
  has_more?: boolean;
  nextOffset: number | null;
  message?: string;
  error?: string | null;
  skip_reason?: string | null;
  errors?: string[];
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export async function getProductFeedbackOverview(
  surveyWave = '2026-q1',
): Promise<ProductFeedbackOverview> {
  const { data, error } = await supabase.rpc('admin_product_feedback_overview', {
    p_survey_wave: surveyWave,
  });
  if (error) throw error;
  return data as ProductFeedbackOverview;
}

export async function listProductFeedbackInvites(
  surveyWave = '2026-q1',
): Promise<ProductFeedbackInviteRow[]> {
  const { data: invites, error } = await supabase
    .from('product_feedback_invites')
    .select('user_id, email, sent_at, send_status, error_message')
    .eq('survey_wave', surveyWave)
    .order('sent_at', { ascending: false })
    .limit(200);

  if (error) throw error;
  if (!invites?.length) return [];

  const userIds = [...new Set(invites.map((i) => i.user_id))];

  const [{ data: users, error: usersErr }, { data: surveys, error: surveysErr }] =
    await Promise.all([
      supabase.from('users').select('user_id, email, name').in('user_id', userIds),
      supabase
        .from('product_feedback_surveys')
        .select('user_id, created_at')
        .eq('survey_wave', surveyWave)
        .in('user_id', userIds)
        .order('created_at', { ascending: false }),
    ]);

  if (usersErr) throw usersErr;
  if (surveysErr) throw surveysErr;

  const userMap = new Map(
    (users ?? []).map((u: { user_id: string; email: string; name: string | null }) => [
      u.user_id,
      u,
    ]),
  );

  const responseMap = new Map<string, string>();
  for (const survey of surveys ?? []) {
    if (!responseMap.has(survey.user_id)) {
      responseMap.set(survey.user_id, survey.created_at);
    }
  }

  return invites.map((invite) => {
    const user = userMap.get(invite.user_id);
    const respondedAt = responseMap.get(invite.user_id) ?? null;
    return {
      user_id: invite.user_id,
      email: invite.email ?? user?.email ?? null,
      name: user?.name ?? null,
      sent_at: invite.sent_at,
      send_status: invite.send_status as 'sent' | 'failed',
      error_message: invite.error_message ?? null,
      has_responded: respondedAt != null,
      responded_at: respondedAt,
    };
  });
}

export async function listProductFeedbackSurveys(
  surveyWave?: string,
): Promise<ProductFeedbackSurveyRow[]> {
  let query = supabase
    .from('product_feedback_surveys')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);
  if (surveyWave) query = query.eq('survey_wave', surveyWave);

  const { data: surveys, error } = await query;
  if (error) throw error;
  const rows = (surveys ?? []) as ProductFeedbackSurvey[];

  const userIds = [...new Set(rows.map((r) => r.user_id))];
  if (userIds.length === 0) return [];

  const { data: users, error: usersErr } = await supabase
    .from('users')
    .select('user_id, email, name')
    .in('user_id', userIds);
  if (usersErr) throw usersErr;

  const userMap = new Map(
    (users ?? []).map((u: { user_id: string; email: string; name: string | null }) => [
      u.user_id,
      u,
    ]),
  );

  return rows.map((row) => {
    const user = userMap.get(row.user_id);
    return {
      ...row,
      user_email: user?.email ?? null,
      user_name: user?.name ?? null,
    };
  });
}

async function invokeFeedbackInvite(body: Record<string, unknown>): Promise<InviteBatchResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('auth_required');

  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-funnel-email`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type: 'product_feedback_invite', ...body }),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload.error ?? payload.message ?? `Invite failed (${res.status})`);
  }
  if ((payload.failed ?? 0) > 0 && (payload.sent ?? 0) === 0 && payload.error) {
    throw new Error(String(payload.error));
  }
  return {
    ok: payload.success ?? payload.ok ?? true,
    sent: payload.sent ?? 0,
    skipped: payload.skipped ?? 0,
    failed: payload.failed ?? 0,
    total: payload.total ?? 0,
    survey_wave: payload.survey_wave,
    has_more: payload.has_more ?? (payload.nextOffset != null),
    nextOffset: payload.nextOffset ?? null,
    message: payload.message,
    error: payload.error ?? null,
    skip_reason: payload.skip_reason ?? null,
    errors: payload.errors,
  } as InviteBatchResult;
}

export async function sendProductFeedbackInviteTest(
  userId: string,
  surveyWave = '2026-q1',
): Promise<InviteBatchResult> {
  return invokeFeedbackInvite({ userId, survey_wave: surveyWave });
}

export async function sendProductFeedbackInviteBatch(
  surveyWave = '2026-q1',
): Promise<InviteBatchResult> {
  return invokeFeedbackInvite({
    survey_wave: surveyWave,
    skip_already_invited: true,
    batch_size: 20,
  });
}

export async function getLatestProductFeedbackSurvey(): Promise<ProductFeedbackSurvey | null> {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('product_feedback_surveys')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as ProductFeedbackSurvey | null) ?? null;
}
